<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useBugReports } from 'dashboard/composables/useBugReports';
import Button from 'next/button/Button.vue';
import BugReportForm from './BugReportForm.vue';
import BugReportDetail from './BugReportDetail.vue';
import { statusBadgeClass } from './statusBadge';
const emit = defineEmits(['recording']);
const route = useRoute();
const { locale } = useI18n();
const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
);
const accountId = computed(() => route.params.accountId);
const {
  uploading,
  uploadError,
  cancelUpload,
  reports,
  selected,
  total,
  busy,
  error,
  unavailable,
  list,
  show,
  create,
  comment,
  download,
  clearSelection,
} = useBugReports(accountId);
const tab = ref('new');
const page = ref(1);
const detail = ref(null);
const draftVersion = ref(0);
const refresh = () => list(page.value);
watch(accountId, () => {
  tab.value = 'new';
  page.value = 1;
  refresh();
});
onMounted(() => {
  refresh();
  window.addEventListener('focus', refresh);
});
onBeforeUnmount(() => window.removeEventListener('focus', refresh));
const submit = async (attributes, files) => {
  if (await create(attributes, files)) {
    draftVersion.value += 1;
    tab.value = 'mine';
  }
};
const reply = async (body, files, submissionId) => {
  if (await comment(body, files, submissionId)) detail.value?.clear();
};
const switchTab = value => {
  if (busy.value) return;
  tab.value = value;
  clearSelection();
  if (value === 'mine') refresh();
};
const changePage = delta => {
  page.value += delta;
  refresh();
};
</script>

<template>
  <section class="bg-n-background">
    <div class="max-w-3xl mx-auto space-y-6">
      <p v-if="unavailable" role="alert">{{ $t('BUG_REPORTS.unavailable') }}</p>
      <template v-else>
        <nav class="flex gap-3" :aria-label="$t('BUG_REPORTS.title')">
          <Button
            v-for="value in ['new', 'mine']"
            :key="value"
            :label="$t(`BUG_REPORTS.${value}`)"
            :disabled="busy"
            :variant="tab === value ? 'solid' : 'ghost'"
            :class="{ '!text-n-slate-12 dark:!text-n-slate-1': tab === value }"
            :aria-current="tab === value ? 'page' : undefined"
            @click="switchTab(value)"
          />
        </nav>
        <p v-if="error || uploadError" role="alert" class="text-n-ruby-11">
          {{
            $t(
              uploadError
                ? `BUG_REPORTS.upload.${uploadError}`
                : 'BUG_REPORTS.error'
            )
          }}
        </p>
        <Button
          v-if="uploading"
          type="button"
          variant="ghost"
          :label="$t('BUG_REPORTS.upload.cancel')"
          @click="cancelUpload"
        />
        <BugReportForm
          v-show="tab === 'new'"
          :key="`${accountId}-${draftVersion}`"
          :busy="busy"
          @submit="submit"
          @recording="emit('recording', $event)"
        />
        <template v-if="tab === 'mine'">
          <BugReportDetail
            v-if="selected"
            :key="`${accountId}-${selected.id}`"
            ref="detail"
            :report="selected"
            :busy="busy"
            @back="
              clearSelection();
              refresh();
            "
            @comment="reply"
            @download="download"
          />
          <div v-else class="space-y-3">
            <p v-if="busy" role="status">{{ $t('BUG_REPORTS.loading') }}</p>
            <p v-else-if="!reports.length">{{ $t('BUG_REPORTS.empty') }}</p>
            <button
              v-for="report in reports"
              :key="report.id"
              type="button"
              class="flex w-full flex-col gap-3 rounded-xl !border !border-solid border-n-slate-6 bg-n-slate-2 p-4 text-left transition-colors hover:border-n-slate-8 hover:bg-n-slate-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              @click="show(report.id)"
            >
              <span
                class="block break-words text-base font-semibold text-n-slate-12"
              >
                {{ report.title }}
              </span>
              <span class="block break-all font-mono text-xs text-n-slate-11">
                {{ report.protocol }}
              </span>
              <span class="flex flex-wrap items-center justify-between gap-3">
                <time
                  :datetime="report.created_at"
                  class="text-sm text-n-slate-11"
                >
                  {{ dateFormatter.format(new Date(report.created_at)) }}
                </time>
                <span class="flex flex-wrap items-center gap-2">
                  <span
                    class="inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium"
                    :class="
                      report.kind === 'bug'
                        ? 'border-n-iris-6 bg-n-iris-3 text-n-iris-11'
                        : 'border-n-slate-6 bg-n-background text-n-slate-11'
                    "
                  >
                    {{ $t(`BUG_REPORTS.values.${report.kind}`) }}
                  </span>
                  <span
                    class="inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold"
                    :class="statusBadgeClass(report.status)"
                  >
                    {{ $t(`BUG_REPORTS.values.${report.status}`) }}
                  </span>
                </span>
              </span>
            </button>
            <div class="flex gap-3">
              <Button
                class="!text-n-slate-12 dark:!text-n-slate-1"
                :label="$t('BUG_REPORTS.previous')"
                :disabled="page <= 1 || busy"
                @click="changePage(-1)"
              />
              <Button
                class="!text-n-slate-12 dark:!text-n-slate-1"
                :label="$t('BUG_REPORTS.next')"
                :disabled="page * 20 >= total || busy"
                @click="changePage(1)"
              />
            </div>
          </div>
        </template>
      </template>
    </div>
  </section>
</template>
